import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';
import { DayOff, Holiday, WorkingTime } from 'src/components';
import { HolidaysType, months, ViewTypes } from 'src/constants/calendar';
import { getWorkTimesForCalendar } from 'src/utils/calendar';

import { Header, MonthView, YearView } from './components';

dayjs.extend(updateLocale);
dayjs.updateLocale('en', {
  weekStart: 1
});

interface CalendarProps {
  workingTimes?: Array<WorkingTime>;
  daysOff?: Array<DayOff>;
  holidays?: Array<Holiday>;
}

const Calendar = memo(({
  workingTimes,
  daysOff,
  holidays,
}: CalendarProps) => {

  // todo move to sort separate handler
  const [selectedViewType, setSelectedViewType] = React.useState(ViewTypes.Year);

  const [yearState, setYearState] = useState(dayjs(workingTimes?.[0]?.period?.from).year());
  const [monthState, setMonthState] = useState(months[9]);

  useEffect(() => {
    if (workingTimes?.[0]?.period?.from) {
      setYearState(dayjs(workingTimes?.[0]?.period?.from).year());
    }
  }, [workingTimes]);

  const handleChangeViewType = (value: ViewTypes) => {
    setSelectedViewType(value);
  };

  const handleHeaderClickBackward = useCallback(() => {
    if (selectedViewType === ViewTypes.Year) {
      setYearState(state => state - 1);
    } else {
      const currentMonthIndex = months.indexOf(monthState);

      if (currentMonthIndex === 0) {
        setMonthState(months[months.length - 1]);
      } else {
        setMonthState(months[currentMonthIndex - 1]);
      }
    }
  }, [selectedViewType, setYearState, monthState, setMonthState]);

  const handleHeaderClickForward = useCallback(() => {
    if (selectedViewType === ViewTypes.Year) {
      setYearState(state => state + 1);
    } else {
      const currentMonthIndex = months.indexOf(monthState);

      if (currentMonthIndex === months.length - 1) {
        setMonthState(months[0]);
      } else {
        setMonthState(months[currentMonthIndex + 1]);
      }
    }
  }, [selectedViewType, setYearState, monthState, setMonthState]);


  // @todo add working with arrays
  // @ts-ignore
  const workTimePeriod = getWorkTimesForCalendar(workingTimes[0] || {});

  const holidayPeriod = {} as any;
  holidays?.forEach((holiday) => {
    const timeKey = dayjs(holiday.date).format('YYYY-MM-DD');
    // @ts-ignore
    holidayPeriod[timeKey] = {
      type: holiday.workHours ? HolidaysType.Shortened : HolidaysType.Free
    };
  });

  const daysOffPeriod = {} as any;
  daysOff?.forEach((dayOff) => {
    const timeKey = dayjs(dayOff.period.from).format('YYYY-MM-DD');
    // @ts-ignore
    daysOffPeriod[timeKey] = {};
  });

  const renderView = useCallback(() => {
    if (selectedViewType === ViewTypes.Year) {
      return (
        <YearView
          workTimePeriod={workTimePeriod}
          holidayPeriod={holidayPeriod}
          daysOffPeriod={daysOffPeriod}
          yearState={yearState}
        />
      );
    } else {
      return (
        <MonthView
          workTimePeriod={workTimePeriod}
          yearState={yearState}
          monthState={monthState}
          holidayPeriod={{
            '2023-01-23': {
              type: HolidaysType.Free
            },
            '2023-09-19': {
              type: HolidaysType.Shortened
            }
          }}
          dayOffPeriod={{
            '2023-02-28': {},
            '2023-09-11': {}
          }}
        />
      );
    }
  }, [selectedViewType, yearState, monthState, workTimePeriod, holidayPeriod, daysOffPeriod]);


  return (
    <Box sx={{
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      justifyContent: 'flex-start',
    }}>
      <Header label={
        selectedViewType === ViewTypes.Year
          ? yearState.toString()
          : `${monthState} ${yearState}`
      }
      selectedViewType={selectedViewType}
      onChangeViewType={handleChangeViewType}
      onClickBackward={handleHeaderClickBackward}
      onClickForward={handleHeaderClickForward}
      />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {
          renderView()
        }
      </LocalizationProvider>
    </Box>
  );
});

export {Calendar}
